import Reveal, { SectionLabel } from "@/components/Reveal";
import { Link } from "react-router-dom";
import { BookOpen, BrainCircuit, LineChart, Calculator, GraduationCap, ArrowRight } from "lucide-react";

const PATHS = [
  {
    num: "01",
    icon: BookOpen,
    title: "Foundations",
    level: "Beginner",
    desc: "Start here — no prior experience assumed.",
    lessons: [
      "TradingView setup & chart basics",
      "Candles, wicks & what price is telling you",
      "Support, resistance & why levels hold",
      "Installing the Atlas indicator",
    ],
  },
  {
    num: "02",
    icon: LineChart,
    title: "Market Structure & Order Flow",
    level: "Core",
    desc: "The engine of the Atlas method.",
    lessons: [
      "Trend, range & break of structure",
      "Liquidity: where stops actually sit",
      "Order blocks & fair value gaps",
      "Reading the Atlas zones top-down",
    ],
  },
  {
    num: "03",
    icon: BrainCircuit,
    title: "Risk & Psychology",
    level: "All levels",
    desc: "The module most traders skip — and need.",
    lessons: [
      "Position sizing & the 1% rule",
      "Building a trade plan you can follow",
      "Drawdown, FOMO & revenge trading",
      "Journaling & reviewing like a professional",
    ],
  },
];

const Education = () => (
    <section id="education" className="relative py-24 lg:py-36 bg-panel/30 border-y border-line/60" data-testid="education-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-14 lg:mb-20">
          <Reveal>
            <SectionLabel>The Atlas Academy</SectionLabel>
            <h2 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-50 leading-tight" data-testid="education-title">
              Tools fade.
              <span className="text-gold-gradient"> Understanding compounds.</span>
            </h2>
            <p className="mt-6 text-sm sm:text-base text-slate-400 leading-relaxed">
              The indicator gets your attention. The education keeps it. Three guided paths take you
              from your first chart to your own independent analysis — every lesson included with
              the Lifetime Access Pass.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-8 space-y-6">
            {PATHS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.1}>
                <div
                  className="border border-line rounded-md bg-card2/40 hover:border-gold/40 transition-colors duration-500 p-6 sm:p-8"
                  data-testid={`education-path-${i + 1}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <span className="font-mono text-xs text-gold/70 tracking-widest mt-1.5">{p.num}</span>
                      <div>
                        <div className="flex items-center gap-3">
                          <p.icon size={18} className="text-gold shrink-0" />
                          <h3 className="font-display font-bold text-lg sm:text-xl text-slate-100 leading-snug">{p.title}</h3>
                        </div>
                        <p className="mt-1.5 text-sm text-slate-500">{p.desc}</p>
                      </div>
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.25em] uppercase border border-gold/30 text-gold/90 px-2.5 py-1 rounded-sm">
                      {p.level}
                    </span>
                  </div>
                  <ol className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {p.lessons.map((l, j) => (
                      <li
                        key={l}
                        className="flex items-baseline gap-3 text-sm text-slate-300"
                        data-testid={`education-path-${i + 1}-lesson-${j + 1}`}
                      >
                        <span className="font-mono text-[10px] text-gold/60 shrink-0">{p.num}.{j + 1}</span>
                        {l}
                      </li>
                    ))}
                  </ol>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="lg:col-span-4 space-y-6">
            <Reveal delay={0.15}>
              <div className="border border-gold/30 rounded-md p-6 bg-gold/5 gold-ring" data-testid="risk-calculator-widget">
                <Calculator size={22} className="text-gold" />
                <h3 className="mt-5 font-display font-bold text-lg text-slate-100 leading-snug">MT5 Position Sizing Calculator</h3>
                <p className="mt-2 text-xs text-slate-400 leading-relaxed">
                  Our full risk-first tool — verified MT5 symbol specs, NO TRADE protection and an
                  optional margin check. Built for gold, indices, crypto and Forex.
                </p>
                <Link
                  to="/academy/position-sizing"
                  data-testid="calculator-tool-cta"
                  className="group mt-5 inline-flex items-center gap-2 bg-gold text-ink font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-gold-bright transition-colors duration-300"
                >
                  Open the calculator
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <p className="mt-3 font-mono text-[10px] tracking-[0.15em] uppercase text-slate-600">Tool 01 · Educational estimator</p>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="border border-line rounded-md p-6 bg-card2/40" data-testid="education-note">
                <GraduationCap size={20} className="text-gold" />
                <h3 className="mt-4 font-display font-bold text-base text-slate-100">Structured, not scattered</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                  No 400-hour video dump. Each path is short, ordered and built around the indicator —
                  and the vault keeps growing as new modules drop for members.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
);

export default Education;
